import { api, withIdempotencyKey } from "@/lib/api/client";
import type { Author } from "@/lib/author";

/** Constantes vitales d'une consultation. Valeurs numériques sérialisées en
 *  chaîne par DRF (DecimalField) ; `bmi` est calculé côté serveur. */
export interface VitalSigns {
  systolic: number | null;
  diastolic: number | null;
  pulse: number | null;
  respiratory_rate: number | null;
  temperature: string | null;
  spo2: number | null;
  weight: string | null;
  height: string | null;
  bmi: string | null;
  measured_at: string;
}

/** Point de la série d'évolution des constantes (courbe sur la fiche patient). */
export interface VitalsSeriesPoint {
  consultation_id: string;
  date: string;
  systolic: number | null;
  diastolic: number | null;
  pulse: number | null;
  respiratory_rate: number | null;
  temperature: string | null;
  spo2: number | null;
  weight: string | null;
  height: string | null;
  bmi: string | null;
}

/** Code CIM-10 (catalogue global). */
export interface Icd10Code {
  id: string;
  code: string;
  label: string;
  category: string;
}

/** Diagnostic d'une consultation (codé CIM-10 ou libre). */
export interface Diagnosis {
  id: string;
  icd10_code: string | null;
  icd10: Icd10Code | null;
  label: string;
  is_primary: boolean;
  note: string;
}

/** Diagnostic envoyé avec la consultation. */
export interface DiagnosisPayload {
  icd10_code?: string | null;
  label?: string;
  is_primary?: boolean;
  note?: string;
}

/** Ligne de morbidité (top diagnostics). */
export interface MorbidityRow {
  label: string;
  code: string | null;
  count: number;
}

/** Motif de consultation (catalogue, suggestions du champ libre). */
export interface ConsultationReason {
  id: string;
  code: string;
  label: string;
}

export interface Consultation {
  id: string;
  /** Référence métier lisible (ex. PDJ-CON-000001). */
  reference: string;
  patient: string;
  patient_code: string;
  patient_name: string;
  patient_age: number | null;
  /** Vide si la fiche n'en porte pas. */
  patient_phone: string;
  practitioner: string;
  practitioner_name: string;
  appointment: string | null;
  consultation_date: string;
  reason: string;
  observations: string;
  conclusion: string;
  duration_minutes: number | null;
  /** Prochain RDV (dérivé de l'ordonnance optique liée). */
  next_appointment_date: string | null;
  /** Une ordonnance optique a-t-elle été émise pour cette consultation ? (statut) */
  has_eye_prescription: boolean;
  /** « PAR » dans l'ancien système : auteur de la saisie. */
  created_by_name: string | null;
  /** Traçabilité auteur (référence/matricule via authorLabel). */
  created_by: Author | null;
  updated_by: Author | null;
  can_delete: boolean;
  /** Constantes vitales (clinique générale). Null si non renseignées. */
  vital_signs: VitalSigns | null;
  /** Diagnostics (clinique générale). Vide si aucun. */
  diagnoses: Diagnosis[];
  /** Facture qui règle la consultation (ADR 0047). Null si non facturée. */
  billing: ConsultationBilling | null;
}

/** Facture vivante la plus récente d'une consultation. */
export interface ConsultationBilling {
  sale: string;
  /** Vide tant que la facture est en brouillon. */
  invoice_number: string;
  status: string;
  total_amount: string;
}

/** Constantes envoyées avec la consultation (champs optionnels). */
export interface VitalSignsPayload {
  systolic?: number | null;
  diastolic?: number | null;
  pulse?: number | null;
  respiratory_rate?: number | null;
  temperature?: number | null;
  spo2?: number | null;
  weight?: number | null;
  height?: number | null;
}

export interface ConsultationListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Consultation[];
}

export interface ConsultationFilters {
  patient?: string;
  search?: string;
  practitioner?: string;
  status?: string;
  /** Filtre par agence (site). */
  site?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  page_size?: number;
  ordering?: string;
}

export interface ConsultationWritePayload {
  patient: string;
  practitioner: string;
  appointment: string | null;
  consultation_date: string;
  reason: string;
  observations: string;
  conclusion: string;
  duration_minutes?: number | null;
  /** Omis = constantes inchangées ; présent = upsert des constantes. */
  vital_signs?: VitalSignsPayload;
  /** Omis = diagnostics inchangés ; présent = remplace l'ensemble. */
  diagnoses?: DiagnosisPayload[];
}

const buildParams = (filters: ConsultationFilters): Record<string, string | number> => {
  const out: Record<string, string | number> = {};
  if (filters.patient) out["patient"] = filters.patient;
  if (filters.search) out["search"] = filters.search;
  if (filters.practitioner) out["practitioner"] = filters.practitioner;
  if (filters.status) out["status"] = filters.status;
  if (filters.site) out["site"] = filters.site;
  if (filters.date_from) out["date_from"] = filters.date_from;
  if (filters.date_to) out["date_to"] = filters.date_to;
  if (filters.page) out["page"] = filters.page;
  if (filters.page_size) out["page_size"] = filters.page_size;
  if (filters.ordering) out["ordering"] = filters.ordering;
  return out;
};

export const consultationsApi = {
  list: async (filters: ConsultationFilters = {}): Promise<ConsultationListResponse> => {
    const { data } = await api.get<ConsultationListResponse>("/consultations/", {
      params: buildParams(filters),
    });
    return data;
  },
  get: async (id: string): Promise<Consultation> => {
    const { data } = await api.get<Consultation>(`/consultations/${id}/`);
    return data;
  },
  create: async (payload: ConsultationWritePayload, idempotencyKey?: string): Promise<Consultation> => {
    const { data } = await api.post<Consultation>("/consultations/", payload, withIdempotencyKey(idempotencyKey));
    return data;
  },
  update: async (id: string, payload: ConsultationWritePayload): Promise<Consultation> => {
    const { data } = await api.patch<Consultation>(`/consultations/${id}/`, payload);
    return data;
  },
  remove: async (id: string): Promise<void> => {
    await api.delete(`/consultations/${id}/`);
  },
  /** Compte rendu PDF de la consultation (ADR 0039). */
  /** Prépare (ou reprend) la facture brouillon de la consultation (ADR 0047). */
  invoice: async (id: string, acts: string[]): Promise<{ id: string }> => {
    const { data } = await api.post<{ id: string }>(`/consultations/${id}/invoice/`, { acts });
    return data;
  },
  report: async (id: string): Promise<Blob> => {
    const { data } = await api.get<Blob>(`/consultations/${id}/report/`, { responseType: "blob" });
    return data;
  },
  vitalsSeries: async (patientId: string): Promise<VitalsSeriesPoint[]> => {
    const { data } = await api.get<VitalsSeriesPoint[]>("/consultations/vitals-series/", {
      params: { patient: patientId },
    });
    return data;
  },
  searchIcd10: async (search: string): Promise<Icd10Code[]> => {
    const { data } = await api.get<Icd10Code[]>("/icd10/", { params: { search } });
    return data;
  },
  morbidity: async (params: { date_from?: string; date_to?: string } = {}): Promise<MorbidityRow[]> => {
    const { data } = await api.get<MorbidityRow[]>("/consultations/morbidity/", { params });
    return data;
  },
  reasons: async (): Promise<ConsultationReason[]> => {
    const { data } = await api.get<ConsultationReason[]>("/consultation-reasons/");
    return data;
  },
};
